/**
 * useLessons — the club's lesson-package catalogue.
 *
 * Plain `useQuery` over the envelope: `success: false` is data, read from
 * `failure`, not `isError` (docs/conventions/internalapi-envelope.md).
 * Server order (price ASC) is preserved — no client sort anywhere.
 *
 * The list depends on who is asking, so it waits for the session. A page
 * loaded directly starts signed out and restores the session a moment later;
 * a catalogue fetched in that gap is the guest's, and it would be cached and
 * shown to the member who is about to appear.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { useClubId } from '~/services/appRuntime';
import { useAuth } from '~/services/auth';
import { listLessons } from '../api/listLessons';
import type { Lesson } from '../types';

export interface UseLessonsOptions {
  enabled?: boolean;
}

// Module scope — referential stability (docs/conventions/react-state.md).
const EMPTY_LESSONS: Lesson[] = [];

export function useLessons(options: UseLessonsOptions = {}) {
  const { enabled = true } = options;
  const [clubIdParam] = useClubId();
  const clubId = clubIdParam ? Number(clubIdParam) : null;
  const { isAuthenticated, isLoading: isRestoringSession } = useAuth();

  const query = useQuery({
    queryKey: clubId
      ? queryKeys.lessons.available(clubId, { asMember: isAuthenticated })
      : queryKeys.disabled.lessons(),
    queryFn: () => {
      if (!clubId) throw new Error('clubId is required');
      return listLessons({ clubId });
    },
    enabled: enabled && !!clubId && !isRestoringSession,
    // Plan data — changes on admin action, not minute-to-minute.
    ...cachePresets.stable,
  });

  const result = query.data;

  return {
    ...query,
    result: result ?? null,
    lessons: result?.success ? result.payload.lessons : EMPTY_LESSONS,
    /** Did an ANSWER arrive? react-state.md — the gate reads this, the words read isLoading. */
    hasAnswer: result !== undefined,
    failure: result && !result.success ? result : null,
  };
}
